import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Home, Compass, Mail } from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import Seo from "@/components/Seo";

const QUICK_LINKS = [
  { to: "/", label: "Home", body: "The full-service concierge story", icon: Home },
  { to: "/services", label: "Services", body: "All services &mdash; strategy to execution", icon: Compass },
  { to: "/services/event-tech", label: "Event Tech", body: "Registration, mobile, hybrid broadcast" },
  { to: "/services/project-management", label: "Project Management", body: "Certified PMP · ATP-I instructor" },
  { to: "/services/hotel-travel", label: "Hotel & Travel", body: "IATAN-accredited group housing" },
  { to: "/services/logistics-planning", label: "Logistics Planning", body: "Site recon to strike" },
  { to: "/services/site-selection", label: "Site Selection", body: "Venues, hotels, destinations" },
  { to: "/services/vip-film", label: "VIP & Film", body: "Executive production &amp; cinema" },
  { to: "/b2b-services", label: "B2B Services", body: "Formation, trademark, brand launch" },
  { to: "/speaker", label: "Sir Dion Magee", body: "Speaker · emcee · facilitator" },
  { to: "/about", label: "About", body: "Agency, portfolio, leadership" },
  { to: "/contact", label: "Contact", body: "Request a quote &middot; book a call" },
];

export default function NotFound() {
  return (
    <div className="bg-[var(--bg)] text-[var(--text)]">
      <Seo
        title="Page not found"
        description="The page you're looking for isn't here. Explore our services, speak with our team, or jump back to the home page."
        path="/404"
        noindex
      />

      <section className="relative pt-36 md:pt-44 pb-16 md:pb-20 border-b border-[var(--border)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-45 pointer-events-none" />
        <div className="brand-bloom brand-bloom-cyan w-[520px] h-[520px] -top-32 -right-32 opacity-35" />
        <HexMark
          size={240}
          opacity={0.055}
          className="absolute -left-10 top-24 rotate-[8deg] pointer-events-none hidden md:block"
        />
        <span className="corner-cross" style={{ top: 40, right: 40 }} />

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="flex items-center gap-4 mb-10 flex-wrap">
              <span className="w-10 h-px bg-[var(--text)]/40" />
              <span className="font-mono-brand text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted)]">
                § HTTP · 404
              </span>
            </div>
            <MaskedReveal
              lines={["Off the map.", "Still on-brand."]}
              className="font-editorial tracking-[-0.03em] leading-[0.94] text-[13vw] md:text-[7.6vw] lg:text-[6.4vw]"
              stagger={0.09}
            />
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-end">
            <Reveal delay={0.35}>
              <p className="text-[var(--text)]/75 text-lg leading-relaxed">
                The page you&rsquo;re looking for isn&rsquo;t here &mdash; but everything below is. Pick a room, or take us straight back to base.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/" className="btn-primary" data-testid="notfound-home">
                  Take me home <ArrowRight size={14} />
                </Link>
                <Link to="/contact" className="btn-dark" data-testid="notfound-contact">
                  <Mail size={14} /> Contact us
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 hex-pattern-dense opacity-30 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="mb-10 md:mb-14">
              <span className="chip">
                <Compass size={12} /> Where to next
              </span>
              <h2 className="mt-6 font-editorial text-3xl md:text-5xl leading-[0.98] tracking-[-0.03em] max-w-2xl">
                Site map,{" "}
                <span className="italic text-cyan-600 font-medium">
                  in one glance.
                </span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {QUICK_LINKS.map((l, i) => (
              <Reveal key={l.to} delay={i * 0.03}>
                <Link
                  to={l.to}
                  data-testid={`notfound-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group relative block bg-white border border-[var(--border)] rounded-2xl p-6 h-full hover:border-[var(--accent)]/45 hover:shadow-[0_16px_40px_-18px_rgba(6,182,212,0.4)] hover:-translate-y-0.5 transition-all duration-500"
                >
                  <span className="corner-cross" style={{ top: 10, right: 10 }} />
                  <div className="flex items-center gap-3">
                    {l.icon ? (
                      <div className="icon-tile !w-10 !h-10">
                        <l.icon size={16} />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-sky-600 text-white text-xs font-mono-brand tracking-[0.16em] flex items-center justify-center">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                    )}
                    <h3 className="font-editorial text-lg md:text-xl leading-tight tracking-tight text-[var(--text)]">
                      {l.label}
                    </h3>
                  </div>
                  <p
                    className="mt-3 text-[13px] text-[var(--text-muted)] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: l.body }}
                  />
                  <span className="mt-4 inline-flex items-center gap-2 font-mono-brand text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] group-hover:translate-x-0.5 transition-transform">
                    Go <ArrowRight size={12} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
